import { datadogRum } from "@datadog/browser-rum";

import { FileUploadAndParseProps } from "@fronterahealth/frontera-ui-components";

import { useCreateReportTemplateLogoMutation, useUpdateReportTemplateLogoMutation } from "@api/graphql/types-and-hooks";
import { notifyError } from "@components/notifications/notifications";

export const useGetS3BucketReportTemplateLogoUrlRetriever: ({
  metadata,
  reportTemplateId,
}: {
  metadata?: object;
  reportTemplateId: string;
}) => FileUploadAndParseProps["S3BucketUrlRetriever"] = ({ metadata: _metadata, reportTemplateId }) => {
  const createFileMutation = useCreateReportTemplateLogoMutation({ throwOnError: false });
  const metadata = _metadata ? JSON.stringify(_metadata) : undefined;

  return async ({ fileKind, fileName, mimeType, fileSize }) => {
    let bucket = "<missing-bucket-url>";
    let fileId = "<missing-file-id>";

    try {
      const res = await createFileMutation.mutateAsync({
        input: {
          fileSize,
          mimeType,
          originalFileName: fileName,
          reportTemplateId: reportTemplateId,
        },
      });

      if (!res.createReportTemplateLogo?.status) {
        const message = res.createReportTemplateLogo?.message;
        notifyError(message || "Error creating file");
        return { bucket, fileId };
      }

      datadogRum.addAction("create-logo-file", {
        fileMetadata: { fileKind, mimeType, fileName, metadata },
      });

      bucket = res?.createReportTemplateLogo?.fileUrl || "<missing-bucket-url>";
      fileId = reportTemplateId || "<missing-file-id>";

      return { bucket, fileId };
    } catch (err) {
      console.error("Error retrieving S3 bucket URL:", err);
      throw new Error(String(err));
    }
  };
};

export const useGetS3BucketUploadReportTemplateLogoCallback: ({
  refetch,
  reportTemplateId,
}: {
  refetch: () => Promise<void> | unknown;
  reportTemplateId: string;
}) => FileUploadAndParseProps["S3BucketUploadCallback"] = ({ refetch, reportTemplateId }) => {
  const updateFileUrlMutation = useUpdateReportTemplateLogoMutation({ throwOnError: false });

  const uploadWithRetry = async ({
    file,
    mimeType,
    url,
    retries = 5,
    attempt = 1,
  }: {
    file: File;
    mimeType: string;
    url: string;
    retries?: number;
    attempt?: number;
  }): Promise<Response> => {
    try {
      console.debug(`Attempt ${attempt}: Uploading file: ${file.name} to S3 URL: ${url}`);

      const response = await fetch(url, {
        method: "PUT",
        headers: { "Content-Type": mimeType },
        body: file,
      });

      if (response.status === 200) {
        return response;
      }

      throw new Error(`S3 upload failed: ${response.status} ${response.statusText}`);
    } catch (err) {
      if (attempt < retries) {
        const backoff = 100 * 2 ** (attempt - 1);
        console.warn(`Upload attempt ${attempt} failed. Retrying in ${backoff}ms...`, err);
        await new Promise((resolve) => setTimeout(resolve, backoff));
        return uploadWithRetry({ file, mimeType, url, retries, attempt: attempt + 1 });
      }

      console.error("Max retries reached. Upload failed.", err);
      throw err;
    }
  };

  return async ({ file, mimeType, url }) => {
    try {
      await uploadWithRetry({ file, mimeType, url });

      datadogRum.addAction("field-file-uploaded-successfully", { fileMetadata: { fileName: file.name, mimeType } });

      const res = await updateFileUrlMutation.mutateAsync({
        input: {
          reportTemplateId: reportTemplateId,
          originalFileName: file.name,
        },
      });

      if (!res?.updateReportTemplateLogo?.status) {
        notifyError(res.updateReportTemplateLogo?.message || "Could not update report template logo url.");
        return;
      }

      await refetch();
    } catch (err) {
      console.error("Error uploading file to S3:", err);
      throw new Error(String(err));
    }
  };
};
